import type { ComponentProps, FunctionComponent, ReactNode } from 'react';
import type { FallbackProps } from 'react-error-boundary';
import { createContext, lazy, Suspense, useContext, useMemo, useState } from 'react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import dayjs from 'dayjs';
import {
  AlertTriangle,
  Calendar,
  ChevronDown,
  CreditCard,
  FileJson2,
  FileText,
  Flag,
  Mail,
  MapPin,
  User,
} from 'lucide-react';
import { ErrorBoundary } from 'react-error-boundary';
import { useImage } from 'react-image';
import { titleCase } from 'string-ts';

import type { IndividualSanctionType } from '@ballerine/common';
import { compareSeverity, INDIVIDUAL_SANCTION_TYPE_SEVERITY, isNullish } from '@ballerine/common';

import type { ImageViewerTransformOptions, ImageViewerTransformState } from '../ImageViewer';
import { ctw } from '@/common';
import {
  AspectRatio,
  Badge,
  Button,
  buttonVariants,
  Card,
  CardContent,
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  DefaultTableCell,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TextWithNAFallback,
} from '@/components';
import { JsonDialog } from '@/components/molecules/JsonDialog/JsonDialog';
import { DataTable } from '@/components/organisms/DataTable/DataTable';
import { CopyButton } from '@/components/templates/shared/CopyButton/CopyButton';
import { ImageViewer } from '../ImageViewer';

const ReactJson = lazy(() => import('react-json-view'));

type Individual = {
  firstName: string | undefined;
  lastName: string | undefined;
  email: string | undefined;
  idType: string | undefined;
  dateOfBirth: string | undefined;
  nationality: string | undefined;
  fullAddress: string | undefined;
};

type DocumentInfo = {
  idNumber: string | undefined;
  number: string | undefined;
  country: string | undefined;
  issuedAt: string | undefined;
  expiresAt: string | undefined;
  placeOfIssue: string | undefined;
  issuer: string | undefined;
  issuingAuthority: string | undefined;
};

type Document = {
  title: string;
  url: string;
  type: string;
};

type Finding = {
  title: string;
  status: string;
};

type SourceEntry = {
  date: string | undefined;
  sourceName: string | undefined;
  sourceUrl: string | undefined;
};

type MatchDetail = {
  type: string;
  sourceName: string | undefined;
  sourceUrl: string | undefined;
  date: string | undefined;
};

type Match = {
  matchedName: string | undefined;
  dateOfBirth?: string | undefined;
  countries: string;
  matchTypes: string;
  aka?: string;
  pep: SourceEntry[];
  warnings: SourceEntry[];
  sanctions: SourceEntry[];
  adverseMedia: SourceEntry[];
  fitnessProbity: SourceEntry[];
  other: SourceEntry[];
  hits?: Array<{ type: string; label: string }>;
  details?: MatchDetail[];
};

type Aml = {
  totalMatches: number;
  fullReport: Record<string, unknown>;
  dateOfCheck: string | undefined;
  matches: Match[];
};

type FieldData = { label: string; value: string | undefined };

export type KycLabels = {
  title: string;
  idvResults: string;
  sanctions: string;
  verifiedBy: string;
  sent: string;
  retrieved: string;
  approved: string;
  rejected: string;
  pending: string;
  match: string;
  matches: string;
  issues: string;
  personalInformation: string;
  documentInformation: string;
  documents: string;
  firstName: string;
  lastName: string;
  email: string;
  idType: string;
  dateOfBirth: string;
  nationality: string;
  fullAddress: string;
  idNumber: string;
  documentNumber: string;
  idCountry: string;
  dateIssued: string;
  validUntil: string;
  placeOfIssue: string;
  issuer: string;
  issuingAuthority: string;
  sanctionsScreening: string;
  totalMatches: string;
  fullReport: string;
  viewFullReport: string;
  dateOfCheck: string;
  scanStatus: string;
  completed: string;
  noResults: string;
  matchedName: string;
  countries: string;
  matchType: string;
  hits: string;
  type: string;
  sourceName: string;
  sourceUrl: string;
  date: string;
  notAvailable: string;
  screenedBy: string;
  warnings: string;
  pep: string;
  adverseMedia: string;
  fitnessProbity: string;
  other: string;
  amlEmptyState: string;
};

export const DEFAULT_KYC_LABELS = {
  title: 'Individual Verification',
  idvResults: 'IDV Results',
  sanctions: 'Sanctions',
  verifiedBy: 'Verified by',
  sent: 'Sent:',
  retrieved: 'Retrieved:',
  approved: 'Approved',
  rejected: 'Rejected',
  pending: 'Pending',
  match: 'Match',
  matches: 'Matches',
  issues: 'Issues',
  personalInformation: 'Personal Information',
  documentInformation: 'Document Information',
  documents: 'Documents',
  firstName: 'First Name',
  lastName: 'Last Name',
  email: 'Email',
  idType: 'ID Type',
  dateOfBirth: 'Date of Birth',
  nationality: 'Nationality',
  fullAddress: 'Full Address',
  idNumber: 'ID Number',
  documentNumber: 'Document Number',
  idCountry: 'ID Country',
  dateIssued: 'Date Issued',
  validUntil: 'Valid Until',
  placeOfIssue: 'Place of Issue',
  issuer: 'Issuer',
  issuingAuthority: 'Issuing Authority',
  sanctionsScreening: 'Sanctions Screening',
  totalMatches: 'Total Matches',
  viewFullReport: 'View Information',
  fullReport: 'Full Report',
  dateOfCheck: 'Date Of Check',
  scanStatus: 'Scan Status',
  completed: 'Completed',
  noResults: 'No results.',
  matchedName: 'Matched Name',
  countries: 'Countries',
  matchType: 'Match Type',
  hits: 'Hits',
  type: 'Type',
  sourceName: 'Source Name',
  sourceUrl: 'Source URL',
  date: 'Date',
  notAvailable: 'N/A',
  screenedBy: 'Screened by',
  warnings: 'Warnings',
  pep: 'PEP',
  adverseMedia: 'Adverse Media',
  fitnessProbity: 'Fitness Probity',
  other: 'Other',
  amlEmptyState: 'If applicable, sanctions screening results will appear here once completed',
} satisfies KycLabels;

type KycContextValue = {
  vendor: string;
  createdAt: string;
  completedAt: string | undefined;
  decision: 'approved' | 'rejected' | 'pending' | undefined;
  findings: Finding[];
  individual: Individual;
  documentInformation: DocumentInfo;
  documents: Document[];
  aml: Aml;
  fullReport: Record<string, unknown>;
  sanctionsCount: number | undefined;
  onViewFullReport?: () => void;
  onViewAmlFullReport?: () => void;
  expandedIndices: Set<number>;
  onToggleExpanded: (index: number) => void;
  selectedDocumentIndex: number;
  onSelectDocument: (index: number) => void;
  labels: KycLabels;
};

const KycContext = createContext<KycContextValue | null>(null);

const useOptionalKycContext = () => useContext(KycContext);

type KycProps = {
  vendor: string;
  createdAt: string;
  completedAt: string | undefined;
  decision: 'approved' | 'rejected' | 'pending' | undefined;
  findings: Finding[];
  individual: Individual;
  documentInformation: DocumentInfo;
  documents: Document[];
  aml: Aml;
  fullReport: Record<string, unknown>;
  sanctionsCount: number | undefined;
  onViewFullReport?: () => void;
  onViewAmlFullReport?: () => void;
  expandedIndices?: Set<number>;
  onToggleExpanded?: (index: number) => void;
  selectedDocumentIndex?: number;
  onSelectDocument?: (index: number) => void;
  labels?: Partial<KycLabels>;
  children: ReactNode;
};

const KycContainer: FunctionComponent<{ children: ReactNode }> = ({ children }) => {
  return (
    <Card>
      <CardContent className="space-y-6 pt-6">{children}</CardContent>
    </Card>
  );
};

type HeaderMeta = {
  vendor: string;
  createdAt: string;
  completedAt: string | undefined;
};

const Header: FunctionComponent<{
  icon?: ReactNode;
  vendor?: string;
  createdAt?: string;
  completedAt?: string | undefined;
  decision?: 'approved' | 'rejected' | 'pending' | undefined;
  sanctionsCount?: number | undefined;
  fullReport?: Record<string, unknown>;
  onViewFullReport?: () => void;
  renderDecisionBadge?: (decision: 'approved' | 'rejected' | 'pending' | undefined) => ReactNode;
  renderSanctionsBadge?: (count: number) => ReactNode;
  renderMeta?: (meta: HeaderMeta) => ReactNode;
  renderFullReport?: (fullReport: Record<string, unknown>) => ReactNode;
}> = ({
  icon: iconProp,
  vendor: vendorProp,
  createdAt: createdAtProp,
  completedAt: completedAtProp,
  decision: decisionProp,
  sanctionsCount: sanctionsCountProp,
  fullReport: fullReportProp,
  onViewFullReport: onViewFullReportProp,
  renderDecisionBadge,
  renderSanctionsBadge,
  renderMeta,
  renderFullReport,
}) => {
  const ctx = useOptionalKycContext();
  const labels = ctx?.labels ?? DEFAULT_KYC_LABELS;
  const vendor = vendorProp ?? ctx?.vendor ?? '';
  const createdAt = createdAtProp ?? ctx?.createdAt ?? '';
  const completedAt = completedAtProp ?? ctx?.completedAt;
  const decision = decisionProp ?? ctx?.decision;
  const sanctionsCount = sanctionsCountProp ?? ctx?.sanctionsCount;
  const fullReport = fullReportProp ?? ctx?.fullReport ?? {};
  const onViewFullReport = onViewFullReportProp ?? ctx?.onViewFullReport;
  const decisionVariant =
    decision === 'approved' ? 'success' : decision === 'rejected' ? 'destructive' : 'warning';
  const sanctionsVariant = (sanctionsCount ?? 0) > 0 ? 'warning' : 'success';

  const defaultDecisionBadge = (d: 'approved' | 'rejected' | 'pending' | undefined) => (
    <Badge
      variant={decisionVariant}
      className="h-[1.4375rem] rounded-sm px-[0.3125rem] py-[0.09375rem] font-bold"
    >
      {d === 'approved' ? labels.approved : d === 'rejected' ? labels.rejected : labels.pending}
    </Badge>
  );

  const defaultSanctionsBadge = (count: number) => (
    <Badge
      variant={sanctionsVariant}
      className="h-[1.4375rem] rounded-sm px-[0.3125rem] py-[0.09375rem] font-bold"
    >
      {count} {count === 1 ? labels.match : labels.matches}
    </Badge>
  );

  const meta: HeaderMeta = { vendor, createdAt, completedAt };

  const defaultMeta = ({ vendor: v, createdAt: created, completedAt: completed }: HeaderMeta) => (
    <>
      <span className="text-muted-foreground">{labels.verifiedBy}</span>
      <span className="text-muted-foreground font-medium">{v}</span>
      <span className="text-muted-foreground">|</span>
      <span className="text-muted-foreground">{labels.sent}</span>
      <span className="text-muted-foreground">{dayjs(created).format('MMM Do YYYY')}</span>
      {completed && (
        <>
          <span className="text-muted-foreground">|</span>
          <span className="text-muted-foreground">{labels.retrieved}</span>
          <span className="text-muted-foreground">{dayjs(completed).format('MMM Do YYYY')}</span>
        </>
      )}
    </>
  );

  const defaultFullReport = (report: Record<string, unknown>) => (
    <div className="flex items-end justify-start">
      <JsonDialog
        buttonProps={{
          variant: 'link',
          className: 'p-0 text-blue-500 h-[unset]',
          onClick: () => onViewFullReport?.(),
        }}
        rightIcon={<FileJson2 size="16" />}
        dialogButtonText={labels.viewFullReport}
        json={JSON.stringify(report)}
      />
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        {iconProp}
        <h2 className="text-2xl font-bold">{labels.title}</h2>
      </div>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">{labels.idvResults}</span>
          {(renderDecisionBadge || defaultDecisionBadge)(decision)}
        </div>
        {sanctionsCount !== undefined && (
          <>
            <span className="text-slate-400">|</span>
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">{labels.sanctions}</span>
              {(renderSanctionsBadge || defaultSanctionsBadge)(sanctionsCount)}
            </div>
          </>
        )}
        {(renderMeta || defaultMeta)(meta)}
        <span className="text-slate-400">|</span>
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">{labels.fullReport}</span>
          {(renderFullReport || defaultFullReport)(fullReport)}
        </div>
      </div>
    </div>
  );
};

const Findings: FunctionComponent<{
  findings?: Finding[];
  renderFinding?: (finding: Finding) => ReactNode;
  children?: (finding: Finding) => ReactNode;
}> = ({ findings: findingsProp, renderFinding, children }) => {
  const ctx = useOptionalKycContext();
  const labels = ctx?.labels ?? DEFAULT_KYC_LABELS;
  const findings = findingsProp ?? ctx?.findings ?? [];

  if (!findings || findings.length === 0) {
    return null;
  }

  const defaultRenderer = (finding: Finding) => {
    const variant =
      finding.status === 'rejected' || finding.status === 'failed' ? 'destructive' : 'warning';

    return (
      <Badge
        key={finding.title}
        variant={variant}
        className="rounded-sm px-4 py-2 text-sm font-medium"
      >
        {finding.title}
      </Badge>
    );
  };

  const render = renderFinding || children || defaultRenderer;

  return (
    <div className="space-y-3">
      <h3 className="text-muted-foreground text-sm font-semibold">{labels.issues}</h3>
      <div className="flex flex-col gap-2">
        {findings.map(finding => (
          <div key={finding.title}>{render(finding)}</div>
        ))}
      </div>
    </div>
  );
};

const Field: FunctionComponent<{
  label: string;
  value: string | undefined;
  copyable?: boolean;
  icon?: ReactNode;
  disableTranslation?: boolean;
}> = ({ label, value, copyable = true, icon, disableTranslation }) => {
  const hasValue = value && value.trim().length > 0;

  return (
    <div className="grid grid-cols-2 gap-4 py-2">
      <div className="flex items-center gap-2 text-sm font-medium text-slate-600">
        {icon && <span className="text-slate-400">{icon}</span>}
        <span>{label}</span>
      </div>
      <div className="flex items-center gap-2">
        <TextWithNAFallback
          className={ctw('text-sm', {
            'text-gray-900': !isNullish(value),
          })}
          disableTranslation={disableTranslation}
        >
          {value}
        </TextWithNAFallback>
        {hasValue && copyable && <CopyButton textToCopy={value} className="h-4 w-4" />}
      </div>
    </div>
  );
};

const PersonalInformation: FunctionComponent<{
  individual?: Individual;
  renderIcon?: () => ReactNode;
  renderTitle?: (title: string) => ReactNode;
  renderField?: (field: FieldData) => ReactNode;
  children?: (field: FieldData) => ReactNode;
}> = ({ individual: individualProp, renderIcon, renderTitle, renderField, children }) => {
  const ctx = useOptionalKycContext();
  const labels = ctx?.labels ?? DEFAULT_KYC_LABELS;
  const individual = individualProp ?? ctx?.individual;

  if (!individual) {
    return null;
  }

  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) return undefined;

    try {
      return dayjs(dateStr).format('D MMMM YYYY');
    } catch {
      return dateStr;
    }
  };

  const fields: Array<FieldData & { icon?: ReactNode; disableTranslation?: boolean }> = [
    {
      label: labels.firstName,
      value: individual.firstName,
      icon: <User className="h-4 w-4" />,
      disableTranslation: true,
    },
    {
      label: labels.lastName,
      value: individual.lastName,
      icon: <User className="h-4 w-4" />,
      disableTranslation: true,
    },
    {
      label: labels.email,
      value: individual.email,
      icon: <Mail className="h-4 w-4" />,
      disableTranslation: true,
    },
    { label: labels.idType, value: individual.idType, icon: <CreditCard className="h-4 w-4" /> },
    {
      label: labels.dateOfBirth,
      value: formatDate(individual.dateOfBirth),
      icon: <Calendar className="h-4 w-4" />,
    },
    {
      label: labels.nationality,
      value: individual.nationality,
      icon: <Flag className="h-4 w-4" />,
    },
    {
      label: labels.fullAddress,
      value: individual.fullAddress,
      icon: <MapPin className="h-4 w-4" />,
    },
  ];

  const defaultRenderer = (
    field: FieldData & { icon?: ReactNode; disableTranslation?: boolean },
  ) => (
    <Field
      key={field.label}
      label={field.label}
      value={field.value}
      icon={field.icon}
      disableTranslation={field.disableTranslation}
    />
  );

  const defaultTitle = () => null;

  const render = renderField || children || defaultRenderer;

  return (
    <div className="space-y-4">
      {(renderIcon || renderTitle) && (
        <div className="flex items-center gap-2">
          {renderIcon?.()}
          {renderTitle?.(labels.personalInformation)}
        </div>
      )}
      <div className="space-y-1">{fields.map(render)}</div>
    </div>
  );
};

const DocumentInformation: FunctionComponent<{
  documentInformation?: DocumentInfo;
  renderIcon?: () => ReactNode;
  renderTitle?: (title: string) => ReactNode;
  renderField?: (field: FieldData) => ReactNode;
  children?: (field: FieldData) => ReactNode;
}> = ({
  documentInformation: documentInformationProp,
  renderIcon,
  renderTitle,
  renderField,
  children,
}) => {
  const ctx = useOptionalKycContext();
  const labels = ctx?.labels ?? DEFAULT_KYC_LABELS;
  const documentInformation = documentInformationProp ?? ctx?.documentInformation;

  if (!documentInformation) {
    return null;
  }

  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) return undefined;

    try {
      return dayjs(dateStr).format('D MMMM YYYY');
    } catch {
      return dateStr;
    }
  };

  const fields: Array<FieldData & { icon?: ReactNode; disableTranslation?: boolean }> = [
    {
      label: labels.idNumber,
      value: documentInformation.idNumber,
      icon: <FileText className="h-4 w-4" />,
      disableTranslation: true,
    },
    {
      label: labels.documentNumber,
      value: documentInformation.number,
      icon: <FileText className="h-4 w-4" />,
      disableTranslation: true,
    },
    {
      label: labels.idCountry,
      value: documentInformation.country,
      icon: <Flag className="h-4 w-4" />,
    },
    {
      label: labels.dateIssued,
      value: formatDate(documentInformation.issuedAt),
      icon: <Calendar className="h-4 w-4" />,
    },
    {
      label: labels.validUntil,
      value: formatDate(documentInformation.expiresAt),
      icon: <Calendar className="h-4 w-4" />,
    },
    {
      label: labels.placeOfIssue,
      value: documentInformation.placeOfIssue,
      icon: <MapPin className="h-4 w-4" />,
    },
    {
      label: labels.issuer,
      value: documentInformation.issuer,
      icon: <FileText className="h-4 w-4" />,
    },
    {
      label: labels.issuingAuthority,
      value: documentInformation.issuingAuthority,
      icon: <FileText className="h-4 w-4" />,
    },
  ];

  const defaultRenderer = (
    field: FieldData & { icon?: ReactNode; disableTranslation?: boolean },
  ) => (
    <Field
      key={field.label}
      label={field.label}
      value={field.value}
      icon={field.icon}
      disableTranslation={field.disableTranslation}
    />
  );

  const render = renderField || children || defaultRenderer;

  return (
    <div className="space-y-4">
      {(renderIcon || renderTitle) && (
        <div className="flex items-center gap-2">
          {renderIcon?.()}
          {renderTitle?.(labels.documentInformation)}
        </div>
      )}
      <div className="space-y-1">{fields.map(render)}</div>
    </div>
  );
};

const ThumbnailSuspenseImage: FunctionComponent<{
  src: string;
  alt: string;
  className?: string;
}> = ({ src, alt, className }) => {
  const { src: loadedSrc } = useImage({
    srcList: src,
    useSuspense: true,
  });

  return <img src={loadedSrc} alt={alt} className={ctw('h-full w-full object-cover', className)} />;
};

const ThumbnailErrorFallback: FunctionComponent<{
  error: Error;
  resetErrorBoundary: () => void;
}> = ({ error: _error, resetErrorBoundary: _resetErrorBoundary }) => {
  return (
    <figure
      aria-live="polite"
      className="flex h-full w-full flex-col items-center justify-center gap-1 bg-slate-100 p-1"
    >
      <AlertTriangle className="h-4 w-4 text-amber-500" aria-hidden="true" />
      <figcaption className="sr-only">Failed to load thumbnail</figcaption>
    </figure>
  );
};

const ThumbnailLoadingFallback: FunctionComponent = () => {
  return (
    <figure
      aria-live="polite"
      aria-busy="true"
      className="flex h-full w-full items-center justify-center bg-slate-100"
    >
      <Skeleton className="h-full w-full rounded-none" />
      <figcaption className="sr-only">Loading thumbnail...</figcaption>
    </figure>
  );
};

const DocumentsThumbnail: FunctionComponent<{
  url?: string;
  title?: string;
  selected?: boolean;
  onClick?: () => void;
  index?: number;
  className?: string;
}> = ({ url, title, selected, onClick, index, className }) => {
  const kycCtx = useOptionalKycContext();

  const resolvedUrl = url ?? kycCtx?.documents[index ?? 0]?.url ?? '';
  const resolvedTitle = title ?? kycCtx?.documents[index ?? 0]?.title ?? '';
  const resolvedType = kycCtx?.documents[index ?? 0]?.type ?? '';
  const resolvedSelected = selected ?? (kycCtx ? index === kycCtx.selectedDocumentIndex : false);
  const resolvedOnClick =
    onClick ?? (kycCtx && index !== undefined ? () => kycCtx.onSelectDocument(index) : undefined);

  return (
    <div className={ctw('flex w-14 flex-col items-center gap-1', className)}>
      <button
        type="button"
        onClick={resolvedOnClick}
        className={ctw('relative w-full overflow-hidden rounded border-2 transition-all', {
          'border-primary': resolvedSelected,
          'border-slate-200': !resolvedSelected,
        })}
      >
        <AspectRatio ratio={1}>
          <div className="absolute inset-0">
            <ErrorBoundary
              fallbackRender={(props: FallbackProps) => (
                <ThumbnailErrorFallback
                  error={props.error}
                  resetErrorBoundary={props.resetErrorBoundary}
                />
              )}
            >
              <Suspense fallback={<ThumbnailLoadingFallback />}>
                <ThumbnailSuspenseImage src={resolvedUrl} alt={resolvedTitle} />
              </Suspense>
            </ErrorBoundary>
          </div>
        </AspectRatio>
      </button>
      {resolvedType && (
        <div className="text-center text-xs text-slate-600">{titleCase(resolvedType ?? '')}</div>
      )}
    </div>
  );
};

const DocumentsThumbnails: FunctionComponent<{
  className?: string;
  children?: (doc: Document, isSelected: boolean, index: number) => ReactNode;
}> = ({ className, children }) => {
  const kycCtx = useOptionalKycContext();
  const documents = kycCtx?.documents ?? [];
  const selectedIndex = kycCtx?.selectedDocumentIndex ?? 0;
  const onSelect = kycCtx?.onSelectDocument ?? (() => {});

  if (documents.length === 0) return null;

  const defaultRenderer = (doc: Document, isSelected: boolean, index: number) => (
    <DocumentsThumbnail
      key={index}
      url={doc.url}
      title={doc.title}
      selected={isSelected}
      onClick={() => onSelect(index)}
      index={index}
    />
  );

  const render = children || defaultRenderer;

  return (
    <Carousel
      opts={{
        align: 'start',
        loop: false,
      }}
      className={ctw('w-full px-10', className)}
    >
      <CarouselContent className="-ml-2">
        {documents.map((doc, index) => (
          <CarouselItem key={index} className="basis-auto pl-2">
            {render(doc, index === selectedIndex, index)}
          </CarouselItem>
        ))}
      </CarouselContent>
      {documents.length > 1 && (
        <>
          <CarouselPrevious className="left-0" />
          <CarouselNext className="right-0" />
        </>
      )}
    </Carousel>
  );
};

type DocumentsProps = {
  transformState?: ImageViewerTransformState;
  onTransformChange?: (state: Partial<ImageViewerTransformState>) => void;
  transformOptions?: ImageViewerTransformOptions;
  onDownload?: () => void;
  onCropApply?: (croppedImageUrl: string) => void;
  children: ReactNode;
};

const DocumentsComponent: FunctionComponent<DocumentsProps> = ({
  transformState,
  onTransformChange,
  transformOptions,
  onDownload,
  onCropApply,
  children,
}) => {
  const kycCtx = useOptionalKycContext();
  const documents = kycCtx?.documents ?? [];
  const selectedIndex = kycCtx?.selectedDocumentIndex ?? 0;
  const selectedDoc = documents[selectedIndex];

  if (!selectedDoc) {
    return null;
  }

  return (
    <ImageViewer
      src={selectedDoc.url}
      alt={selectedDoc.title}
      transformState={transformState}
      onTransformChange={onTransformChange}
      transformOptions={transformOptions}
      onDownload={onDownload}
      onCropApply={onCropApply}
    >
      {children}
    </ImageViewer>
  );
};

type DocumentsType = FunctionComponent<DocumentsProps> & {
  Container: typeof ImageViewer.Container;
  Caption: typeof ImageViewer.Caption;
  Viewport: typeof ImageViewer.Viewport;
  Toolbar: typeof ImageViewer.Toolbar;
  ZoomControls: typeof ImageViewer.ZoomControls;
  RotationControls: typeof ImageViewer.RotationControls;
  CropControls: typeof ImageViewer.CropControls;
  ResetButton: typeof ImageViewer.ResetButton;
  DownloadButton: typeof ImageViewer.DownloadButton;
  FullscreenButton: typeof ImageViewer.FullscreenButton;
  RetryButton: typeof ImageViewer.RetryButton;
  Divider: typeof ImageViewer.Divider;
  Status: typeof ImageViewer.Status;
  ErrorFallback: typeof ImageViewer.ErrorFallback;
  LoadingFallback: typeof ImageViewer.LoadingFallback;
  FullscreenDialog: typeof ImageViewer.FullscreenDialog;
  Thumbnails: typeof DocumentsThumbnails;
  Thumbnail: typeof DocumentsThumbnail;
};

const Documents = DocumentsComponent as DocumentsType;

// Use lazy getters to defer ImageViewer property access until runtime
// This prevents "Cannot access 'ImageViewer' before initialization" errors
Object.defineProperty(Documents, 'Container', {
  get: () => ImageViewer.Container,
  enumerable: true,
});
Object.defineProperty(Documents, 'Caption', {
  get: () => ImageViewer.Caption,
  enumerable: true,
});
Object.defineProperty(Documents, 'Viewport', {
  get: () => ImageViewer.Viewport,
  enumerable: true,
});
Object.defineProperty(Documents, 'Toolbar', {
  get: () => ImageViewer.Toolbar,
  enumerable: true,
});
Object.defineProperty(Documents, 'ZoomControls', {
  get: () => ImageViewer.ZoomControls,
  enumerable: true,
});
Object.defineProperty(Documents, 'RotationControls', {
  get: () => ImageViewer.RotationControls,
  enumerable: true,
});
Object.defineProperty(Documents, 'CropControls', {
  get: () => ImageViewer.CropControls,
  enumerable: true,
});
Object.defineProperty(Documents, 'ResetButton', {
  get: () => ImageViewer.ResetButton,
  enumerable: true,
});
Object.defineProperty(Documents, 'DownloadButton', {
  get: () => ImageViewer.DownloadButton,
  enumerable: true,
});
Object.defineProperty(Documents, 'FullscreenButton', {
  get: () => ImageViewer.FullscreenButton,
  enumerable: true,
});
Object.defineProperty(Documents, 'RetryButton', {
  get: () => ImageViewer.RetryButton,
  enumerable: true,
});
Object.defineProperty(Documents, 'Divider', {
  get: () => ImageViewer.Divider,
  enumerable: true,
});
Object.defineProperty(Documents, 'Status', {
  get: () => ImageViewer.Status,
  enumerable: true,
});
Object.defineProperty(Documents, 'ErrorFallback', {
  get: () => ImageViewer.ErrorFallback,
  enumerable: true,
});
Object.defineProperty(Documents, 'LoadingFallback', {
  get: () => ImageViewer.LoadingFallback,
  enumerable: true,
});
Object.defineProperty(Documents, 'FullscreenDialog', {
  get: () => ImageViewer.FullscreenDialog,
  enumerable: true,
});
// Thumbnails and Thumbnail are local components, not ImageViewer properties, so direct assignment is safe
Documents.Thumbnails = DocumentsThumbnails;
Documents.Thumbnail = DocumentsThumbnail;

type AmlContextValue = {
  aml: Aml;
  totalMatches: number;
  expandedIndices: Set<number>;
  onToggleExpanded: (index: number) => void;
  onViewAmlFullReport: (() => void) | undefined;
  vendor: string | undefined;
};

const AmlContext = createContext<AmlContextValue | null>(null);

const AmlHeader: FunctionComponent<{
  title?: string;
  className?: string;
}> = ({ title, className }) => {
  const kycCtx = useOptionalKycContext();
  const labels = kycCtx?.labels ?? DEFAULT_KYC_LABELS;

  return (
    <h3 className={ctw('text-lg font-semibold', className)}>
      {title ?? labels.sanctionsScreening}
    </h3>
  );
};

const AmlSummary: FunctionComponent<{
  vendor?: string;
  totalMatches?: number;
  dateOfCheck?: string | undefined;
  fullReport?: Record<string, unknown>;
  onViewAmlFullReport?: () => void;
  props?: {
    table?: ComponentProps<typeof Table>;
  };
}> = props => {
  const ctx = useContext(AmlContext);
  const kycCtx = useOptionalKycContext();
  const labels = kycCtx?.labels ?? DEFAULT_KYC_LABELS;

  const vendor = props.vendor ?? ctx?.vendor;
  const totalMatches = props.totalMatches ?? ctx?.totalMatches ?? 0;
  const dateOfCheck = props.dateOfCheck ?? ctx?.aml.dateOfCheck;
  const fullReport = props.fullReport ?? ctx?.aml.fullReport;
  const onViewAmlFullReport = props.onViewAmlFullReport ?? ctx?.onViewAmlFullReport;

  type SummaryRow = {
    screenedBy: string;
    totalMatches: number;
    fullReport: Record<string, unknown> | undefined;
    dateOfCheck: string | undefined;
    scanStatus: string;
  };
  const columnHelper = createColumnHelper<SummaryRow>();
  const columns = useMemo(
    () => [
      columnHelper.accessor('screenedBy', {
        header: labels.screenedBy,
        cell: info => {
          const screenedBy = info.getValue();

          return <TextWithNAFallback>{screenedBy}</TextWithNAFallback>;
        },
      }),
      columnHelper.accessor('totalMatches', {
        header: labels.totalMatches,
        cell: info => {
          const value = info.getValue();
          const cellVariant = value === 0 ? 'success' : 'warning';

          return (
            <Badge variant={cellVariant} className="mb-1 rounded-lg px-2 py-1 font-bold">
              {value} {value === 1 ? labels.match : labels.matches}
            </Badge>
          );
        },
      }),
      columnHelper.accessor('fullReport', {
        header: labels.fullReport,
        cell: info => {
          const value = info.getValue();

          return (
            <div className={`flex items-end justify-start`}>
              <JsonDialog
                buttonProps={{
                  variant: 'link',
                  className: 'p-0 text-blue-500 h-[unset]',
                  onClick: () => onViewAmlFullReport?.(),
                }}
                rightIcon={<FileJson2 size={`16`} />}
                dialogButtonText={`View Information`}
                json={JSON.stringify(value)}
              />
            </div>
          );
        },
      }),
      columnHelper.accessor('dateOfCheck', {
        header: labels.dateOfCheck,
        cell: info => {
          const value = info.getValue();

          return (
            <TextWithNAFallback>
              {value ? dayjs(value).format('D MMMM YYYY') : labels.notAvailable}
            </TextWithNAFallback>
          );
        },
      }),
      columnHelper.accessor('scanStatus', {
        header: labels.scanStatus,
        cell: info => {
          const value = info.getValue();

          return (
            <Badge variant="success" className="mb-1 rounded-lg px-2 py-1 font-bold">
              {value}
            </Badge>
          );
        },
      }),
    ],
    [
      labels.screenedBy,
      labels.totalMatches,
      labels.fullReport,
      labels.dateOfCheck,
      labels.scanStatus,
      labels.match,
      labels.matches,
      labels.notAvailable,
    ],
  );

  const data = useMemo(
    () => [
      {
        screenedBy: vendor ? titleCase(vendor ?? '') : '',
        totalMatches,
        fullReport,
        dateOfCheck,
        scanStatus: labels.completed,
      },
    ],
    [vendor, totalMatches, fullReport, dateOfCheck, labels.completed],
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: {
      cell: DefaultTableCell,
    },
  });

  return (
    <Table {...props.props?.table} className={ctw('my-8', props.props?.table?.className)}>
      <TableHeader>
        {table.getHeaderGroups()?.map(headerGroup => (
          <TableRow key={headerGroup.id} className={ctw('hover:bg-unset border-none')}>
            {headerGroup.headers?.map((header, index) => (
              <TableHead
                key={header.id}
                className={ctw(
                  'text-foreground !h-[unset] !pl-3 pb-2 pt-0 text-sm font-medium leading-none',
                  {
                    '!pl-3.5': index === 0,
                  },
                )}
              >
                {!header.isPlaceholder &&
                  flexRender(header.column.columnDef.header, header.getContext())}
              </TableHead>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>
        {!!table.getRowModel().rows?.length &&
          table.getRowModel().rows?.map(row => (
            <TableRow key={row.id} className={ctw('hover:bg-unset h-6 border-none')}>
              {row.getVisibleCells()?.map(cell => (
                <TableCell key={cell.id} className={ctw('!py-px !pl-3.5')}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
      </TableBody>
    </Table>
  );
};

const AmlMatchRow: FunctionComponent<{
  match: Match;
  index: number;
  expanded?: boolean;
  onToggle?: () => void;
  renderHits?: (hits: Array<{ type: string; label: string }>) => ReactNode;
  renderExpanded?: (match: Match) => ReactNode;
  children?: (match: Match) => ReactNode;
}> = ({
  match,
  index,
  expanded: expandedProp,
  onToggle: onToggleProp,
  renderHits,
  renderExpanded,
  children,
}) => {
  const ctx = useContext(AmlContext);
  const kycCtx = useOptionalKycContext();
  const labels = kycCtx?.labels ?? DEFAULT_KYC_LABELS;

  const expanded = expandedProp ?? ctx?.expandedIndices.has(index) ?? false;
  const onToggle = onToggleProp ?? (ctx ? () => ctx.onToggleExpanded(index) : undefined);

  const defaultHits = (hits: Array<{ type: string; label: string }>) =>
    hits.map(hit => {
      const severity = INDIVIDUAL_SANCTION_TYPE_SEVERITY[hit.type as IndividualSanctionType];
      const variant = severity === 'critical' ? 'destructive' : 'warning';

      return (
        <Badge key={hit.type} variant={variant} className="text-xs font-medium">
          {hit.label}
        </Badge>
      );
    });

  const defaultExpanded = (m: Match) => (
    <div className="space-y-3">
      <div className="grid grid-cols-4 gap-4 px-4 text-sm font-semibold text-slate-600">
        <div>{labels.type}</div>
        <div>{labels.sourceName}</div>
        <div>{labels.sourceUrl}</div>
        <div>{labels.date}</div>
      </div>
      {m.details?.map((detail, detailIndex) => (
        <div key={detailIndex} className="grid grid-cols-4 gap-4 px-4 text-sm">
          <div>{detail.type}</div>
          <div>{detail.sourceName || labels.notAvailable}</div>
          <div>
            {detail.sourceUrl ? (
              <a
                href={detail.sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                {detail.sourceUrl}
              </a>
            ) : (
              labels.notAvailable
            )}
          </div>
          <div>{detail.date || labels.notAvailable}</div>
        </div>
      ))}
    </div>
  );

  const hasDetails = match.details && match.details.length > 0;
  const expandedRenderer = renderExpanded || children || defaultExpanded;

  return (
    <>
      <tr className="border-b hover:bg-slate-50">
        <td className="p-2">
          {hasDetails && (
            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={onToggle}>
              <ChevronDown
                className={ctw('h-4 w-4 transition-transform', {
                  'rotate-180': expanded,
                })}
              />
            </Button>
          )}
        </td>
        <td className="p-2 font-semibold">{index + 1}</td>
        <td className="p-2">{match.matchedName}</td>
        <td className="p-2">{match.countries}</td>
        <td className="p-2">
          <TextWithNAFallback>{titleCase(match.matchTypes ?? '')}</TextWithNAFallback>
        </td>
        <td className="p-2">
          <div className="flex flex-wrap gap-1">
            {(renderHits || defaultHits)(
              match.hits ??
                (() => {
                  const hits: Array<{ type: string; label: string }> = [];

                  if (match.fitnessProbity.length > 0)
                    hits.push({ type: 'fitnessProbity', label: labels.fitnessProbity });

                  if (match.sanctions.length > 0)
                    hits.push({ type: 'sanctions', label: labels.sanctions });

                  if (match.pep.length > 0) hits.push({ type: 'pep', label: labels.pep });

                  if (match.adverseMedia.length > 0)
                    hits.push({ type: 'adverseMedia', label: labels.adverseMedia });

                  if (match.warnings.length > 0)
                    hits.push({ type: 'warnings', label: labels.warnings });

                  if (match.other.length > 0) hits.push({ type: 'other', label: labels.other });

                  return hits;
                })(),
            )}
          </div>
        </td>
      </tr>
      {expanded && hasDetails && (
        <tr>
          <td colSpan={6} className="bg-slate-50 p-4">
            {expandedRenderer(match)}
          </td>
        </tr>
      )}
    </>
  );
};

const AmlMatchDetail: FunctionComponent<{
  match: Match;
  categoryOrder?: Array<
    'pep' | 'warnings' | 'sanctions' | 'adverseMedia' | 'fitnessProbity' | 'other'
  >;
}> = ({ match, categoryOrder }) => {
  const kycCtx = useOptionalKycContext();
  const labels = kycCtx?.labels ?? DEFAULT_KYC_LABELS;

  type CategoryKey = 'pep' | 'warnings' | 'sanctions' | 'adverseMedia' | 'fitnessProbity' | 'other';

  const orderedTypes: Array<{ key: CategoryKey; header: string }> = categoryOrder
    ? categoryOrder.map(key => ({
        key,
        header:
          key === 'pep'
            ? labels.pep
            : key === 'warnings'
              ? labels.warnings
              : key === 'sanctions'
                ? labels.sanctions
                : key === 'adverseMedia'
                  ? labels.adverseMedia
                  : key === 'fitnessProbity'
                    ? labels.fitnessProbity
                    : labels.other,
      }))
    : [
        { key: 'fitnessProbity' as const, header: labels.fitnessProbity },
        { key: 'sanctions' as const, header: labels.sanctions },
        { key: 'pep' as const, header: labels.pep },
        { key: 'adverseMedia' as const, header: labels.adverseMedia },
        { key: 'warnings' as const, header: labels.warnings },
        { key: 'other' as const, header: labels.other },
      ];

  return (
    <div className="flex flex-col gap-y-2">
      <div className="flex gap-x-10 px-4 font-semibold">
        <span className="w-[100px] shrink-0 text-left">{labels.type}</span>
        <span className="min-w-0 flex-1 text-left">{labels.sourceName}</span>
        <span className="w-[200px] shrink-0 text-left">{labels.sourceUrl}</span>
        <span className="w-[100px] shrink-0 text-left">{labels.date}</span>
      </div>
      {orderedTypes.map(type =>
        match[type.key].map((item: SourceEntry, index: number) => (
          <div key={`${type.key}-${index}`} className="flex gap-x-10 px-4">
            <span className="w-[100px] shrink-0 text-left">{type.header}</span>
            <TextWithNAFallback className="min-w-0 flex-1 text-left">
              {item.sourceName}
            </TextWithNAFallback>
            <TextWithNAFallback className="w-[200px] shrink-0 truncate text-left">
              {item.sourceUrl && (
                <a
                  className={buttonVariants({
                    variant: 'link',
                    className: 'h-[unset] cursor-pointer !p-0 !text-blue-500',
                  })}
                  target="_blank"
                  rel="noopener noreferrer"
                  href={item.sourceUrl}
                  title={item.sourceUrl}
                >
                  {item.sourceUrl}
                </a>
              )}
            </TextWithNAFallback>
            <TextWithNAFallback className="w-[100px] shrink-0 text-left">
              {item.date && dayjs(item.date).format('MMM DD, YYYY')}
            </TextWithNAFallback>
          </div>
        )),
      )}
    </div>
  );
};

const AmlEmptyState: FunctionComponent<{
  message?: string;
  className?: string;
}> = ({ message, className }) => {
  const kycCtx = useOptionalKycContext();
  const labels = kycCtx?.labels ?? DEFAULT_KYC_LABELS;

  return <p className={ctw('py-4 text-slate-500', className)}>{message ?? labels.amlEmptyState}</p>;
};

const AmlMatches: FunctionComponent<{
  matches?: Match[];
  expandedIndices?: Set<number>;
  onToggleExpanded?: (index: number) => void;
  renderRow?: (match: Match, index: number) => ReactNode;
  renderHeader?: () => ReactNode;
  renderEmptyState?: () => ReactNode;
  children?: (match: Match, index: number) => ReactNode;
  scrollClassName?: string;
  cellClassName?: string;
  props?: {
    dataTable?: {
      select?: ComponentProps<typeof DataTable>['select'];
      props?: ComponentProps<typeof DataTable>['props'];
    };
  };
  CollapsibleContent?: FunctionComponent<{ row: Match }>;
}> = props => {
  const ctx = useContext(AmlContext);
  const kycCtx = useOptionalKycContext();
  const labels = kycCtx?.labels ?? DEFAULT_KYC_LABELS;

  const getHits = (match: Match): Array<{ type: IndividualSanctionType; label: string }> => {
    if (match.hits) return match.hits as Array<{ type: IndividualSanctionType; label: string }>;

    const hits: Array<{ type: IndividualSanctionType; label: string }> = [];

    if (match.fitnessProbity.length > 0)
      hits.push({ type: 'fitnessProbity', label: labels.fitnessProbity });

    if (match.sanctions.length > 0) hits.push({ type: 'sanctions', label: labels.sanctions });

    if (match.pep.length > 0) hits.push({ type: 'pep', label: labels.pep });

    if (match.adverseMedia.length > 0)
      hits.push({ type: 'adverseMedia', label: labels.adverseMedia });

    if (match.warnings.length > 0) hits.push({ type: 'warnings', label: labels.warnings });

    if (match.other.length > 0) hits.push({ type: 'other', label: labels.other });

    return hits;
  };

  const matches = useMemo(() => {
    const source = props.matches ?? ctx?.aml.matches ?? [];

    return [...source]
      .map((match, originalIndex) => ({ match, originalIndex }))
      .sort((a, b) => {
        const sev = compareSeverity(getMatchSeverity(a.match), getMatchSeverity(b.match));

        return sev !== 0 ? sev : a.originalIndex - b.originalIndex;
      })
      .map(({ match }) => match);
  }, [props.matches, ctx?.aml.matches]);

  function getMatchSeverity(match: Match) {
    const orderedKeys: IndividualSanctionType[] = [
      'fitnessProbity',
      'sanctions',
      'pep',
      'adverseMedia',
      'warnings',
      'other',
    ];
    const presentKey = orderedKeys.find(key => match[key]?.length > 0);

    return presentKey ? INDIVIDUAL_SANCTION_TYPE_SEVERITY[presentKey] : 'low';
  }
  const expandedIndices = useMemo(
    () => props.expandedIndices ?? ctx?.expandedIndices ?? new Set<number>(),
    [props.expandedIndices, ctx?.expandedIndices],
  );
  const onToggleExpanded = props.onToggleExpanded ?? ctx?.onToggleExpanded ?? (() => {});

  const columnHelper = createColumnHelper<Match>();

  if (!matches || matches.length === 0) {
    if (props.renderEmptyState) {
      return <>{props.renderEmptyState()}</>;
    }

    return (
      <div className="rounded-lg border p-4 text-center text-slate-500">{labels.noResults}</div>
    );
  }

  const columns = [
    columnHelper.display({
      id: 'collapsible',
      cell: ({ row }) => (
        <Button
          onClick={e => {
            e.stopPropagation();
            row.toggleExpanded();
          }}
          disabled={row.getCanExpand()}
          variant="ghost"
          size="icon"
          className="p-[7px]"
        >
          <ChevronDown
            className={ctw('d-4', {
              'rotate-180': row.getIsExpanded(),
            })}
          />
        </Button>
      ),
    }),
    columnHelper.display({
      id: 'index',
      cell: info => {
        const index = info.cell.row.index + 1;

        return <TextWithNAFallback className="p-1 font-semibold">{index}</TextWithNAFallback>;
      },
      header: '#',
    }),
    columnHelper.accessor('matchedName', {
      header: labels.matchedName,
      cell: info => <TextWithNAFallback>{info.getValue()}</TextWithNAFallback>,
      enableSorting: false,
    }),
    columnHelper.accessor('countries', {
      header: labels.countries,
      cell: info => <TextWithNAFallback>{info.getValue()}</TextWithNAFallback>,
      enableSorting: false,
    }),
    columnHelper.accessor('matchTypes', {
      header: labels.matchType,
      cell: info => {
        const matchTypes = info.getValue();

        return <TextWithNAFallback>{titleCase(matchTypes ?? '')}</TextWithNAFallback>;
      },
      enableSorting: false,
    }),
    columnHelper.display({
      id: 'hits',
      header: labels.hits,
      cell: info => {
        const match = info.row.original;
        const hits = getHits(match);

        if (hits.length === 0) {
          return <TextWithNAFallback />;
        }

        return (
          <div className="flex flex-wrap gap-2">
            {hits.map(hit => (
              <Badge
                key={hit.type}
                variant={
                  INDIVIDUAL_SANCTION_TYPE_SEVERITY[hit.type] === 'critical'
                    ? 'destructive'
                    : 'warning'
                }
                className="cursor-pointer rounded-lg px-2 py-1 font-medium"
              >
                {hit.label}
              </Badge>
            ))}
          </div>
        );
      },
      enableSorting: false,
    }),
  ];

  // If render props are provided, use the legacy manual table implementation
  if (props.renderRow || props.renderHeader || props.children) {
    const defaultHeader = () => (
      <tr className="border-b bg-slate-50">
        <th className="p-2 text-left text-sm font-semibold text-slate-600"></th>
        <th className="p-2 text-left text-sm font-semibold text-slate-600">#</th>
        <th className="p-2 text-left text-sm font-semibold text-slate-600">{labels.matchedName}</th>
        <th className="p-2 text-left text-sm font-semibold text-slate-600">{labels.countries}</th>
        <th className="p-2 text-left text-sm font-semibold text-slate-600">{labels.matchType}</th>
        <th className="p-2 text-left text-sm font-semibold text-slate-600">{labels.hits}</th>
      </tr>
    );

    const defaultRow = (match: Match, index: number) => (
      <AmlMatchRow
        key={index}
        match={match}
        index={index}
        expanded={expandedIndices.has(index)}
        onToggle={onToggleExpanded ? () => onToggleExpanded(index) : undefined}
      />
    );

    const rowRenderer = props.renderRow || props.children || defaultRow;

    return (
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>{(props.renderHeader || defaultHeader)()}</thead>
          <tbody>{matches.map((match, index) => rowRenderer(match, index))}</tbody>
        </table>
      </div>
    );
  }

  // Default: use DataTable
  const CollapsibleContent =
    props.CollapsibleContent ?? (({ row }: { row: Match }) => <AmlMatchDetail match={row} />);

  return (
    <DataTable
      select={{
        onSelect: () => {
          // No-op: selection not used for AML matches
        },
        selected: {},
        ...props.props?.dataTable?.select,
      }}
      props={{
        scroll: {
          className: ctw(
            '[&>div]:max-h-[50vh]',
            props.props?.dataTable?.props?.scroll?.className ?? '',
          ),
          ...props.props?.dataTable?.props?.scroll,
        },
        cell: {
          className: ctw('!p-0', props.props?.dataTable?.props?.cell?.className ?? ''),
          ...props.props?.dataTable?.props?.cell,
        },
        ...props.props?.dataTable?.props,
      }}
      options={{
        getRowId: (_row, index) => index?.toString() ?? '',
      }}
      data={matches}
      columns={columns}
      CollapsibleContent={CollapsibleContent}
    />
  );
};

const AmlComponent: FunctionComponent<{
  aml?: Aml;
  vendor?: string;
  onViewAmlFullReport?: () => void;
  expandedIndices?: Set<number>;
  onToggleExpanded?: (index: number) => void;
  children?: ReactNode;
}> = ({
  aml: amlProp,
  vendor: vendorProp,
  onViewAmlFullReport: onViewAmlFullReportProp,
  expandedIndices: expandedIndicesProp,
  onToggleExpanded: onToggleExpandedProp,
  children,
}) => {
  const kycCtx = useOptionalKycContext();
  const aml = amlProp ?? kycCtx?.aml;
  const vendor = vendorProp ?? kycCtx?.vendor;
  const onViewAmlFullReport = onViewAmlFullReportProp ?? kycCtx?.onViewAmlFullReport;
  const expandedIndices = expandedIndicesProp ?? kycCtx?.expandedIndices ?? new Set<number>();
  const onToggleExpanded = onToggleExpandedProp ?? kycCtx?.onToggleExpanded ?? (() => {});

  if (!aml) {
    return null;
  }

  const totalMatches = aml.totalMatches;

  const ctxValue: AmlContextValue = {
    aml,
    totalMatches,
    expandedIndices,
    onToggleExpanded,
    onViewAmlFullReport,
    vendor,
  };

  const isScreeningCompleted =
    aml.dateOfCheck !== undefined || (aml.matches && aml.matches.length > 0);

  return (
    <AmlContext.Provider value={ctxValue}>
      <div className="space-y-4">
        {children}
        {!children && (
          <>
            <AmlHeader />
            {isScreeningCompleted && (
              <>
                <AmlSummary />
                <AmlMatches />
              </>
            )}
            {!isScreeningCompleted && <AmlEmptyState />}
          </>
        )}
      </div>
    </AmlContext.Provider>
  );
};

const AmlWithSubComponents = Object.assign(AmlComponent, {
  Header: AmlHeader,
  Summary: AmlSummary,
  Matches: AmlMatches,
  MatchRow: AmlMatchRow,
  MatchDetail: AmlMatchDetail,
  EmptyState: AmlEmptyState,
});

type FullReportProps = {
  fullReport?: Record<string, unknown>;
  theme?: 'bright' | 'monokai' | 'ocean' | 'twilight';
  className?: string;
  collapsed?: boolean | number;
  enableClipboard?: boolean;
};

const FullReport: FunctionComponent<FullReportProps> = ({
  fullReport: fullReportProp,
  theme = 'bright',
  className,
  collapsed = 2,
  enableClipboard = false,
}) => {
  const kycCtx = useOptionalKycContext();

  // Fallback chain: prop → Kyc context
  const fullReport = fullReportProp ?? kycCtx?.fullReport ?? {};

  return (
    <div className={className}>
      <Suspense
        fallback={
          <div className="flex items-center justify-center p-8">
            <Skeleton className="h-8 w-8 rounded-full" />
          </div>
        }
      >
        <ReactJson
          name={null}
          theme={theme}
          src={fullReport}
          collapsed={collapsed}
          enableClipboard={enableClipboard}
          displayDataTypes={false}
          displayObjectSize={false}
        />
      </Suspense>
    </div>
  );
};

type FullReportDialogProps = {
  // Controlled state
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  // Data (falls back to Kyc context)
  fullReport?: Record<string, unknown>;
  // Dialog props
  title?: string;
  description?: string;
  className?: string;
  contentClassName?: string;
  // Render prop OR children
  children?: ReactNode | ((fullReport: Record<string, unknown>) => ReactNode);
};

const FullReportDialog: FunctionComponent<FullReportDialogProps> = ({
  open: openProp,
  onOpenChange: onOpenChangeProp,
  fullReport: fullReportProp,
  title,
  description,
  className,
  contentClassName,
  children,
}) => {
  const kycCtx = useOptionalKycContext();
  const labels = kycCtx?.labels ?? DEFAULT_KYC_LABELS;

  // Internal state for uncontrolled usage
  const [internalOpen, setInternalOpen] = useState(false);

  // Resolve controlled vs uncontrolled
  const isOpen = openProp ?? internalOpen;
  const handleOpenChange = onOpenChangeProp ?? setInternalOpen;

  // Resolve fullReport from prop or context
  const fullReport = fullReportProp ?? kycCtx?.fullReport ?? {};

  // Determine what to render inside the dialog
  const renderContent = () => {
    if (typeof children === 'function') {
      // Render prop pattern: children(fullReport)
      return children(fullReport);
    }

    if (children) {
      // Regular children
      return children;
    }

    // Default: render <FullReport />
    return <FullReport />;
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        className={ctw(
          'flex h-full max-h-[95vh] w-full !max-w-[95vw] flex-col p-0 sm:!max-w-[95vw]',
          contentClassName,
          className,
        )}
      >
        <DialogHeader className="flex-shrink-0 px-6 pb-0 pt-6">
          <DialogTitle>{title ?? labels.fullReport}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <div className="min-h-0 flex-1 overflow-auto p-6">{renderContent()}</div>
      </DialogContent>
    </Dialog>
  );
};

export const Kyc: FunctionComponent<KycProps> & {
  Container: typeof KycContainer;
  Header: typeof Header;
  Findings: typeof Findings;
  PersonalInformation: typeof PersonalInformation;
  DocumentInformation: typeof DocumentInformation;
  Documents: typeof Documents;
  Field: typeof Field;
  Aml: typeof AmlWithSubComponents;
  FullReportDialog: typeof FullReportDialog;
  FullReport: typeof FullReport;
} = ({
  vendor,
  createdAt,
  completedAt,
  decision,
  findings,
  individual,
  documentInformation,
  documents,
  aml,
  fullReport,
  sanctionsCount,
  onViewFullReport,
  onViewAmlFullReport,
  expandedIndices = new Set(),
  onToggleExpanded = () => {},
  selectedDocumentIndex = 0,
  onSelectDocument = () => {},
  labels: labelsProp,
  children,
}) => {
  const labels = { ...DEFAULT_KYC_LABELS, ...labelsProp };

  const ctxValue: KycContextValue = {
    vendor,
    createdAt,
    completedAt,
    decision,
    findings,
    individual,
    documentInformation,
    documents,
    aml,
    fullReport,
    sanctionsCount,
    onViewFullReport,
    onViewAmlFullReport,
    expandedIndices,
    onToggleExpanded,
    selectedDocumentIndex,
    onSelectDocument,
    labels,
  };

  return <KycContext.Provider value={ctxValue}>{children}</KycContext.Provider>;
};

Kyc.Container = KycContainer;
Kyc.Header = Header;
Kyc.Findings = Findings;
Kyc.PersonalInformation = PersonalInformation;
Kyc.DocumentInformation = DocumentInformation;
Kyc.Documents = Documents;
Kyc.Field = Field;
Kyc.Aml = AmlWithSubComponents;
Kyc.FullReportDialog = FullReportDialog;
Kyc.FullReport = FullReport;
